import { BoxGeometry, type BufferGeometry } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { type LaneSpec, laneGeometry } from '../world/laneBuilder';
import {
  makeHalfPipe,
  makeQuarterPipe,
  makeRamp,
  makeSpire,
  makeSurfPrism,
  makeWallRamp,
} from '../world/ramps';

/** The prebuilt ramp shapes the editor can place. */
export type ShapeId =
  | 'surfPrism'
  | 'ramp'
  | 'wallRamp'
  | 'spire'
  | 'platform'
  | 'curveL90'
  | 'curveR90'
  | 'curveL45'
  | 'curveR45'
  | 'sbend'
  | 'corkscrew'
  | 'halfpipe'
  | 'quarterpipe'
  | 'transferGap';

/**
 * How a placed mesh behaves at play time: which level material it wears and
 * whether it collides. Mirrors the named-mesh roles in `glbLevel.ts`.
 */
export type ShapeRole = 'ramp' | 'platform' | 'ground' | 'detail';

export interface ShapeDef {
  id: ShapeId;
  label: string;
  /** Default play-time role (a placement may override it). */
  role: ShapeRole;
  /** Default dimensions, also the editable parameter set for a shape. */
  defaults: Record<string, number>;
  geometry(params: Record<string, number>): BufferGeometry;
}

/**
 * Catalog of placeable shapes. Each wraps one of the procedural ramp factories
 * the game itself uses, so what you place in the editor is exactly what you
 * surf. Keep this the single source of truth for shape ids + default sizes.
 */
/** Reads a param, falling back to its default (params is index-typed). */
const n = (p: Record<string, number>, key: string, fallback: number): number => p[key] ?? fallback;

/**
 * Lane spec for the curve/turn shapes — the single source of truth shared by the
 * geometry factory and the course generator (which needs each curve's true exit
 * via `buildLane`). Returns null for non-lane shapes.
 */
export function laneSpecFor(shapeId: ShapeId, p: Record<string, number>): LaneSpec | null {
  switch (shapeId) {
    case 'curveL90':
    case 'curveR90':
    case 'curveL45':
    case 'curveR45':
      return {
        width: n(p, 'width', 768),
        angleDeg: n(p, 'faceAngle', 56),
        declineDeg: n(p, 'decline', 10),
        segments: [
          { length: n(p, 'lead', 500) },
          { length: n(p, 'arcLength', 2600), turnDeg: n(p, 'turnDeg', 90) },
          { length: n(p, 'runout', 500) },
        ],
      };
    case 'sbend':
      return {
        width: n(p, 'width', 768),
        angleDeg: n(p, 'faceAngle', 56),
        declineDeg: n(p, 'decline', 9),
        segments: [
          { length: 900 },
          { length: 1400, turnDeg: -32 },
          { length: 700 },
          { length: 1400, turnDeg: 32 },
          { length: 900 },
        ],
      };
    case 'corkscrew':
      return {
        width: n(p, 'width', 720),
        angleDeg: n(p, 'faceAngle', 58),
        declineDeg: n(p, 'decline', 12),
        segments: [
          { length: 1000 },
          { length: n(p, 'arcLength', 4500), turnDeg: n(p, 'turnDeg', -150) },
          { length: 900 },
        ],
      };
    default:
      return null;
  }
}

function laneGeo(shapeId: ShapeId, p: Record<string, number>): BufferGeometry {
  const spec = laneSpecFor(shapeId, p);
  if (!spec) {
    throw new Error(`laneGeo: ${shapeId} is not a lane shape`);
  }
  return laneGeometry(spec);
}

const BANK_DEFAULTS = { width: 768, faceAngle: 56, decline: 10, lead: 500, runout: 500 };

export const SHAPES: Record<ShapeId, ShapeDef> = {
  surfPrism: {
    id: 'surfPrism',
    label: 'Surf prism',
    role: 'ramp',
    defaults: { length: 4096, width: 768, angleDeg: 55 },
    geometry: (p) =>
      makeSurfPrism({
        length: n(p, 'length', 4096),
        width: n(p, 'width', 768),
        angleDeg: n(p, 'angleDeg', 55),
        caps: n(p, 'caps', 1) !== 0, // caps:0 lets chained pieces meet without a seam wall
      }),
  },
  ramp: {
    id: 'ramp',
    label: 'Wedge ramp',
    role: 'ramp',
    defaults: { length: 2048, width: 1024, angleDeg: 45 },
    geometry: (p) =>
      makeRamp({
        length: n(p, 'length', 2048),
        width: n(p, 'width', 1024),
        angleDeg: n(p, 'angleDeg', 45),
      }),
  },
  wallRamp: {
    id: 'wallRamp',
    label: 'Wall ramp',
    role: 'ramp',
    defaults: { length: 4096, height: 960, angleDeg: 55 },
    geometry: (p) =>
      makeWallRamp({
        length: n(p, 'length', 4096),
        height: n(p, 'height', 960),
        angleDeg: n(p, 'angleDeg', 55),
        caps: n(p, 'caps', 1) !== 0, // caps:0 lets chained pieces meet without a seam wall
      }),
  },
  spire: {
    id: 'spire',
    label: 'Spire',
    role: 'ramp',
    defaults: { baseRadius: 640, topRadius: 120, height: 1200 },
    geometry: (p) =>
      makeSpire({
        baseRadius: n(p, 'baseRadius', 640),
        topRadius: n(p, 'topRadius', 120),
        height: n(p, 'height', 1200),
      }),
  },
  platform: {
    id: 'platform',
    label: 'Platform',
    role: 'platform',
    defaults: { width: 768, height: 64, depth: 768 },
    geometry: (p) => new BoxGeometry(n(p, 'width', 768), n(p, 'height', 64), n(p, 'depth', 768)),
  },
  curveL90: {
    id: 'curveL90',
    label: 'Curve L 90°',
    role: 'ramp',
    defaults: { ...BANK_DEFAULTS, turnDeg: 90, arcLength: 2600 },
    geometry: (p) => laneGeo('curveL90', p),
  },
  curveR90: {
    id: 'curveR90',
    label: 'Curve R 90°',
    role: 'ramp',
    defaults: { ...BANK_DEFAULTS, turnDeg: -90, arcLength: 2600 },
    geometry: (p) => laneGeo('curveR90', p),
  },
  curveL45: {
    id: 'curveL45',
    label: 'Curve L 45°',
    role: 'ramp',
    defaults: { ...BANK_DEFAULTS, turnDeg: 45, arcLength: 1500 },
    geometry: (p) => laneGeo('curveL45', p),
  },
  curveR45: {
    id: 'curveR45',
    label: 'Curve R 45°',
    role: 'ramp',
    defaults: { ...BANK_DEFAULTS, turnDeg: -45, arcLength: 1500 },
    geometry: (p) => laneGeo('curveR45', p),
  },
  sbend: {
    id: 'sbend',
    label: 'S-bend',
    role: 'ramp',
    defaults: { width: 768, faceAngle: 56, decline: 9 },
    geometry: (p) => laneGeo('sbend', p),
  },
  corkscrew: {
    id: 'corkscrew',
    label: 'Corkscrew',
    role: 'ramp',
    defaults: { width: 720, faceAngle: 58, decline: 12, arcLength: 4500, turnDeg: -150 },
    geometry: (p) => laneGeo('corkscrew', p),
  },
  halfpipe: {
    id: 'halfpipe',
    label: 'Half-pipe',
    role: 'ramp',
    defaults: { length: 4096, radius: 1100, halfArcDeg: 75 },
    geometry: (p) =>
      makeHalfPipe({
        length: n(p, 'length', 4096),
        radius: n(p, 'radius', 1100),
        halfArcDeg: n(p, 'halfArcDeg', 75),
      }),
  },
  quarterpipe: {
    id: 'quarterpipe',
    label: 'Quarter-pipe',
    role: 'ramp',
    defaults: { length: 4096, radius: 1100, arcDeg: 82 },
    geometry: (p) =>
      makeQuarterPipe({
        length: n(p, 'length', 4096),
        radius: n(p, 'radius', 1100),
        arcDeg: n(p, 'arcDeg', 82),
      }),
  },
  transferGap: {
    id: 'transferGap',
    label: 'Transfer gap',
    role: 'ramp',
    defaults: { segLength: 2200, gap: 640, width: 768, faceAngle: 55 },
    geometry: (p) => {
      const seg = n(p, 'segLength', 2200);
      const opts = { length: seg, width: n(p, 'width', 768), angleDeg: n(p, 'faceAngle', 55) };
      const first = makeSurfPrism(opts);
      const second = makeSurfPrism(opts).translate(0, 0, seg + n(p, 'gap', 640));
      const merged = mergeGeometries([first, second], false);
      if (!merged) {
        throw new Error('transferGap: merge failed');
      }
      return merged;
    },
  },
};

export const SHAPE_IDS = Object.keys(SHAPES) as ShapeId[];
